import React from 'react';
import { ChevronDown, ChevronUp, Building2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { cn } from '../lib/utils';

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors" data-testid="btn-toggle-foro">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-lg">{title}</span>
          {badge && <span className="px-2 py-0.5 bg-[#aa2aff] text-white text-xs rounded-full">{badge}</span>}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && <div className="p-4 bg-[#080c2d]/50">{children}</div>}
    </div>
  );
}

export function ForoSection({ isOpen, onToggle, data = {}, onChange, formatPrice, currency, settings = {} }) {
  const priceSantaFe = settings.price_foro_santa_fe || 1000;
  const priceMasivo = settings.price_foro_masivo || 30000;
  const priceStorage = settings.price_foro_storage || 97;

  const santaFeTotal = priceSantaFe * (data.santaFeHoras || 0);
  const masivoTotal = priceMasivo * (data.masivoDias || 0);
  const storageTotal = priceStorage * (data.storageDias || 0);
  const subtotal = santaFeTotal + masivoTotal + storageTotal;

  const handleChange = (field, value) => onChange({ ...data, [field]: value });
  const badge = subtotal > 0 ? formatPrice(subtotal) : null;

  return (
    <CollapsibleSection title="Foro" icon={Building2} isOpen={isOpen} onToggle={onToggle} badge={badge}>
      <div className="space-y-3">
        {/* Foro Santa Fe */}
        <div className={cn("p-4 rounded-xl border transition-all", data.santaFeHoras > 0 ? "border-[#aa2aff] bg-[#aa2aff]/10" : "border-white/10 bg-white/5")}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h4 className="text-white font-semibold text-sm">Foro Santa Fe</h4>
              <p className="text-slate-400 text-xs mt-1">80m² de Foro</p>
              <p className="text-slate-400 text-xs mt-0.5">{formatPrice(priceSantaFe)} {currency} / hora</p>
            </div>
            <div className="text-center">
              <Label className="text-slate-500 text-[10px]">Horas</Label>
              <Input type="number" min="0" max="99" value={data.santaFeHoras || ''} onChange={(e) => handleChange('santaFeHoras', parseInt(e.target.value) || 0)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white w-20 h-8 text-sm focus:border-[#aa2aff]" data-testid="foro-santa-fe-horas" />
            </div>
          </div>
          {santaFeTotal > 0 && <p className="text-[#aa2aff] text-xs font-semibold mt-2">= {formatPrice(santaFeTotal)} {currency}</p>}
        </div>

        {/* Foro Masivo */}
        <div className={cn("p-4 rounded-xl border transition-all", data.masivoDias > 0 ? "border-[#aa2aff] bg-[#aa2aff]/10" : "border-white/10 bg-white/5")}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h4 className="text-white font-semibold text-sm">Foro Masivo</h4>
              <p className="text-slate-400 text-xs mt-1">800m² de producción</p>
              <p className="text-slate-400 text-xs mt-0.5">{formatPrice(priceMasivo)} {currency} / día</p>
            </div>
            <div className="text-center">
              <Label className="text-slate-500 text-[10px]">Días</Label>
              <Input type="number" min="0" max="99" value={data.masivoDias || ''} onChange={(e) => handleChange('masivoDias', parseInt(e.target.value) || 0)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white w-20 h-8 text-sm focus:border-[#aa2aff]" data-testid="foro-masivo-dias" />
            </div>
          </div>
          {masivoTotal > 0 && <p className="text-[#aa2aff] text-xs font-semibold mt-2">= {formatPrice(masivoTotal)} {currency}</p>}
        </div>

        {/* Storage */}
        <div className={cn("p-4 rounded-xl border transition-all", data.storageDias > 0 ? "border-[#aa2aff] bg-[#aa2aff]/10" : "border-white/10 bg-white/5")}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <h4 className="text-white font-semibold text-sm">Storage / Almacenamiento</h4>
              <p className="text-slate-400 text-xs mt-1">Almacenamiento por hasta 8m³ (2x2x2). Incluye manejo y armado/desarmado de set.</p>
              <p className="text-slate-400 text-xs mt-0.5">{formatPrice(priceStorage)} {currency} / día</p>
            </div>
            <div className="text-center">
              <Label className="text-slate-500 text-[10px]">Días</Label>
              <Input type="number" min="0" max="365" value={data.storageDias || ''} onChange={(e) => handleChange('storageDias', parseInt(e.target.value) || 0)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white w-20 h-8 text-sm focus:border-[#aa2aff]" data-testid="foro-storage-dias" />
            </div>
          </div>
          {storageTotal > 0 && <p className="text-[#aa2aff] text-xs font-semibold mt-2">= {formatPrice(storageTotal)} {currency}</p>}
        </div>

        {subtotal > 0 && (
          <div className="pt-3 border-t border-white/10 flex justify-between items-center">
            <span className="text-white font-semibold">Subtotal Foro:</span>
            <span className="text-[#aa2aff] font-bold text-lg">{formatPrice(subtotal)} {currency}</span>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export function calcForoTotal(data = {}, settings = {}) {
  return (settings.price_foro_santa_fe || 1000) * (data.santaFeHoras || 0) + (settings.price_foro_masivo || 30000) * (data.masivoDias || 0) + (settings.price_foro_storage || 97) * (data.storageDias || 0);
}
